import { Injectable, signal, effect } from '@angular/core';

export type DemoTheme = 'light' | 'dark';

const STORAGE_KEY = 'demo-theme';

/**
 * Singleton (providedIn: 'root') theme service.
 * Mirrors the anti-flash inline script in index.html: reads localStorage,
 * defaults to the light travel design, and keeps document.documentElement
 * in sync via an effect.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly initial: DemoTheme = this.readInitialTheme();
  readonly theme = signal<DemoTheme>(this.initial);

  constructor() {
    effect(() => {
      const value = this.theme();
      document.documentElement.dataset['theme'] = value;
      try {
        localStorage.setItem(STORAGE_KEY, value);
      } catch {
        // localStorage may be unavailable (private mode); ignore.
      }
    });
  }

  toggle(): void {
    this.theme.set(this.theme() === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: DemoTheme): void {
    this.theme.set(theme);
  }

  private readInitialTheme(): DemoTheme {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // ignore
    }
    return 'light';
  }
}
